/**
 * Ответ ♪ одним тостом (проба P50m10d, правка E6): после ♪ на странице не было ни успеха, ни ошибки — переводчик
 * не знал, принят ли ♪, новый ли дубль и сколько он стоил. Теперь:
 *   * принят — «♪ принят: «текст»», под ним — новый дубль и его знаки ElevenLabs (или прежний дубль из кэша за 0),
 *     длина было → стало, голос («зал · Michael»), правка уходит в эфир сама;
 *   * ♪ озвучил, но в эфир правка не отправлена или слова дубля не совпали — то же, предупреждением, с причиной;
 *   * ошибка — словами сервера (их же пишет красная полоса редактора).
 * Сведения о дубле — поле ответа `take` (translate-python `stream_voice_forward.take_answer`); старый сервер его не
 * шлёт — тогда знаки не выдумываются.
 */
import { pluralRu } from "../../utils/pluralRu";
import { cueSpeakerLabel, cueSpeakerOf } from "./cueSpeaker";
import { voicingCheckOf } from "./voicingCheck";

export interface RevoiceToast {
    type: "success" | "warning" | "error";
    message: string;
    description: string;
}

const num = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const clip = (text: string, max = 60): string => {
    const one = text.replace(/\s+/g, " ").trim();
    return one.length > max ? `${one.slice(0, max - 1)}…` : one;
};

const charsRu = (n: number): string => `${n} ${pluralRu(n, "знак", "знака", "знаков")}`;

export const revoiceReply = (result: unknown, en = false): RevoiceToast => {
    const r = (result && typeof result === "object" ? result : {}) as Record<string, any>;
    if (!result || typeof r.error === "string") {
        const why = typeof r.error === "string" && r.error.trim() ? r.error.trim() : en ? "no answer from the server" : "сервер не ответил";
        return { type: "error", message: en ? "♪ failed — the cue was not voiced" : "♪ не озвучил реплику", description: why };
    }
    const parts: string[] = [];
    let type: RevoiceToast["type"] = "success";
    const take = r.take && typeof r.take === "object" ? (r.take as Record<string, any>) : null;
    if (take?.new === true) {
        const paid = num(take.paid_chars) ? take.paid_chars : 0;
        parts.push(en ? `new take — ${paid} ElevenLabs chars` : `новый дубль — ${charsRu(paid)} ElevenLabs`);
    } else if (take?.new === false) {
        parts.push(en ? "the previous take from the cache — 0 ElevenLabs chars" : "прежний дубль из кэша — 0 знаков ElevenLabs");
    } else {
        parts.push(en ? "new take ready" : "новый дубль готов");
    }
    if (num(take?.length_before_ms) && num(take?.length_after_ms)) {
        parts.push(en ? `length ${take!.length_before_ms} → ${take!.length_after_ms} ms` : `длина ${take!.length_before_ms} → ${take!.length_after_ms} мс`);
    } else if (num(r.length)) {
        parts.push(en ? `length ${Math.round(r.length)} ms` : `длина ${Math.round(r.length)} мс`);
    }
    const speaker = cueSpeakerOf(r);
    if (speaker) parts.push(`${en ? "voice" : "голос"}: ${cueSpeakerLabel(speaker, en)}`);
    if (Array.isArray(r.stream_fixes)) {
        const bad = r.stream_fixes.find((fix: any) => fix && typeof fix.error === "string");
        if (bad) {
            type = "warning";
            parts.push(en ? `not sent on air: ${bad.error}` : `в эфир не отправлен: ${bad.error}`);
        } else if (r.stream_fixes.length) {
            const id = r.stream_fixes[0]?.id ? ` ${r.stream_fixes[0].id}` : "";
            parts.push(en ? `fix${id} accepted — goes on air by itself` : `правка${id} принята — уйдёт в эфир сама`);
        }
    }
    const check = voicingCheckOf(r.voicing_check);
    if (check && check.ok === false) {
        type = "warning";
        parts.push(en ? "the take's words do not match the text — see the red bar" : "слова дубля не совпали с текстом — см. красную полосу");
    }
    if (r.passed === false) {
        type = "warning";
        parts.push(en ? "the take failed the check — see the red bar" : "дубль не прошёл проверку — см. красную полосу");
    }
    const text = typeof r.captions === "string" && r.captions.trim() ? ` «${clip(r.captions)}»` : "";
    return { type, message: `${en ? "♪ accepted" : "♪ принят"}${text}`, description: parts.join("; ") };
};
